import React, { useCallback, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, FluentProvider, Spinner, webLightTheme } from "@fluentui/react-components";
import { disablePush, enablePush, pushStatus, type PushStatus } from "./spurt/push";
import { notifications } from "./spurt/notifications";

const STATUS_TEXT: Record<PushStatus, string> = {
  unsupported: "This browser can't show notifications while Spurt is closed.",
  blocked: "Notifications are blocked for this site. Allow them in your browser's site settings, then come back.",
  off: "Off. You only get alerts while Spurt is open.",
  on: "On. You'll get alerts for messages, calls and meetings even when Spurt is closed.",
  unconfigured: "Push alerts aren't set up on this server yet. Ask your administrator.",
};

interface Props {
  open: boolean;
  onClose: () => void;
  toast: (m: string) => void;
}

const NotificationSettings: React.FC<Props> = ({ open, onClose, toast }) => {
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    pushStatus()
      .then(setStatus)
      .catch(() => setStatus("unsupported"));
  }, []);
  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      const r = await fn();
      if (typeof r === "string") setStatus(r as PushStatus);
      else refresh();
      if (ok) toast(ok);
    } catch {
      toast("That didn't work. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <FluentProvider theme={webLightTheme} style={{ display: "contents" }}>
      <Dialog open={open} modalType="modal" onOpenChange={(_, d) => !d.open && onClose()}>
        <DialogSurface style={{ zIndex: 2147483000 }}>
          <DialogBody>
            <DialogTitle>Notifications</DialogTitle>
            <DialogContent>
              <h3 style={{ margin: "0 0 4px", fontSize: 14 }}>Alerts on this device</h3>
              <p role="status" style={{ margin: "0 0 12px", color: "#424242" }}>{status ? STATUS_TEXT[status] : <Spinner size="tiny" label="Checking…" />}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {status === "off" && (
                  <Button appearance="primary" disabled={busy} onClick={() => run(enablePush)}>
                    Turn on alerts
                  </Button>
                )}
                {status === "on" && (
                  <Button disabled={busy} onClick={() => run(disablePush, "Alerts turned off on this device")}>
                    Turn off alerts
                  </Button>
                )}
                <Button disabled={busy} onClick={() => run(() => notifications.sendTest(), "Test notification sent")}>
                  Send a test notification
                </Button>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </FluentProvider>
  );
};

export default NotificationSettings;
